import styled from "styled-components";

export const PageHeader = styled.header`
  background: var(--surface);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1rem 0 0.875rem;
`;

export const Breadcrumb = styled.nav`
  font-size: 0.8125rem;
  color: var(--text-label);
  margin-bottom: 0.25rem;

  a {
    color: var(--primary);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
  span { margin: 0 0.375rem; }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  color: var(--text-label);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 0.375rem;
  transition: color 0.15s ease, background 0.15s ease;
  font-size: 1.25rem;

  &:hover { color: var(--text-secondary); background: rgba(0,0,0,0.05); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

export const PageTitle = styled.h1`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DateBadge = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
`;

export const CnabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.45rem 1rem;
  border-radius: 2rem;
  border: none;
  background: var(--primary);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--primary-hover); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  i { font-size: 1rem; }
`;

export const CnabHint = styled.small`
  display: block;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-label);
  text-align: right;
  margin-top: 0.25rem;
`;

/* ── Summary bar ─────────────────────────────── */
export const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  background: var(--surface-2);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: var(--border-radius-sm);
  margin-bottom: 1rem;
`;

export const SummaryItem = styled.div`
  .s-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-label);
    margin-bottom: 0.125rem;
  }
  .s-value {
    font-size: 1.125rem;
    font-weight: 800;
    color: var(--primary);
  }
  .s-count {
    font-size: 0.8125rem;
    color: var(--text-muted);
    margin-top: 0.125rem;
  }
`;

/* ── Quote Card (same design system as ProfessionalQuoteCard) ── */
export const Card = styled.article`
  background: var(--white);
  border-radius: var(--border-radius);
  border: 1px solid rgba(0,0,0,0.06);
  border-left: 4px solid var(--status-color, var(--border));
  box-shadow: var(--shadow-sm);
  margin-bottom: 0.5rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  overflow: hidden;

  &:hover {
    box-shadow: var(--shadow);
    transform: translateY(-1px);
  }
`;

export const CardHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  flex-wrap: wrap;
`;

export const QuoteId = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-label);
  white-space: nowrap;
  font-family: monospace;
  letter-spacing: 0.03em;
`;

export const PatientName = styled.h2`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
`;

export const DetailsButton = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.4rem 1rem;
  border-radius: 2rem;
  background: var(--primary);
  color: #fff;
  text-decoration: none;
  transition: var(--transition);
  white-space: nowrap;
  cursor: pointer;

  &:hover { background: var(--primary-hover); color: #fff; text-decoration: none; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
  i { font-size: 1rem; }

  &.has-pending { background: var(--danger); }
`;

export const CardBody = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 1rem;
  padding: 0.5rem 1rem 0.625rem;
  border-top: 1px solid rgba(0,0,0,0.05);
  border-bottom: 1px solid rgba(0,0,0,0.05);

  @media (min-width: 576px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 992px) {
    grid-template-columns: repeat(5, auto);
    justify-content: start;
    gap: 0 2.5rem;
  }
`;

export const MetaItem = styled.div`
  padding: 0.375rem 0;
`;

export const MetaLabel = styled.p`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-label);
  margin: 0 0 0.125rem;
  display: flex; align-items: center; gap: 0.25rem;
`;

export const MetaValue = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--text-primary);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetaValueMuted = styled(MetaValue)`
  color: var(--text-label); font-weight: 400; font-style: italic;
`;

export const MetaValueDanger = styled(MetaValue)`
  color: var(--danger);
  display: flex; align-items: center; gap: 0.2rem;
`;

export const MetaValuePrimary = styled(MetaValue)`
  color: var(--primary);
`;

export const MetaSub = styled.small`
  font-size: 0.6875rem;
  color: var(--text-label);
  display: block;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

export const CardFooter = styled.footer`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  flex-wrap: wrap;
`;

export const PaymentBadge = styled.span<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.1875rem 0.625rem;
  border-radius: 2rem;
  text-transform: uppercase;
  background: ${({ $dark }) => $dark ? "rgba(0,0,0,0.07)" : "rgba(var(--primary-rgb),0.1)"};
  color: ${({ $dark }) => $dark ? "var(--text-secondary)" : "var(--primary)"};
  border: 1px solid ${({ $dark }) => $dark ? "rgba(0,0,0,0.1)" : "rgba(var(--primary-rgb),0.2)"};
  i { font-size: 0.875rem; }
`;

export const DocIndicator = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.1875rem 0.5rem;
  border-radius: 2rem;
  transition: opacity 0.15s ease;
  opacity: ${({ $active }) => ($active ? 1 : 0.35)};
  color: ${({ $active }) => ($active ? "#15803d" : "var(--gray)")};
  background: ${({ $active }) => ($active ? "rgba(21,128,61,0.08)" : "transparent")};
  border: 1px solid ${({ $active }) => ($active ? "rgba(21,128,61,0.2)" : "rgba(0,0,0,0.1)")};
  i { font-size: 0.875rem; }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-label);
  text-align: center;
  i { font-size: 3rem; margin-bottom: 1rem; opacity: 0.35; }
  p { font-size: 0.9375rem; margin: 0; }
`;
